const theme = {
    text: {
        color: '#ffffff',
        fontFamily: 'Roboto Mono',
        fontSize: 3.4,
        fontWeight: 700,
        radius: 0.66,
        orientation: 'radial',
        align: 'middle',
        overflow: 'ellipsis',
        strokeColor: '#000000',
        strokeWidth: 0.2,
    },
    border: {
        color: '#ffffff',
        width: 0.4,
    },
    dividers: {
        color: '#ffffff',
        width: 0.2,
    },
};

export const getWheelThemeAssetUrl = (path) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const publicUrl = process.env.PUBLIC_URL;

    if (!publicUrl || publicUrl === '.' || publicUrl === './') {
        return normalizedPath;
    }

    return `${publicUrl.replace(/\/$/, '')}${normalizedPath}`;
};

export const WHEEL_THEME_POINTER_URLS = {
    neon: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/neon/neon-pointer.svg`,
    realisticSpace: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/space/realistic-space-pointer.svg`,
};

const themeVideoMedia = (path) => {
    const url = `${process.env.REACT_APP_CDN_BASE_URL}${path}`;

    return {
        name: url,
        type: 'video/external',
        url,
        kind: 'video',
    };
};

const SAFARI_THEME_WEBM_PATTERN = /\/auction\/themes\/(?:neon\/neon|space\/realistic-space)-(?:frame|center)\.webm(?=[?#]|$)/i;

export const isSafariBrowser = (userAgent) => {
    const browserUserAgent = typeof userAgent === 'string'
        ? userAgent
        : typeof navigator !== 'undefined'
            ? navigator.userAgent || ''
            : '';

    return /AppleWebKit/i.test(browserUserAgent)
        && !/(?:Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS|Android)/i.test(browserUserAgent);
};

export const getWheelThemeSafariMediaUrl = (src, asset) => {
    if (typeof src !== 'string' || !src.trim()) {
        return null;
    }

    const explicitSafariUrl = typeof asset?.safariUrl === 'string'
        ? asset.safariUrl.trim()
        : '';

    if (explicitSafariUrl && explicitSafariUrl !== src) {
        return explicitSafariUrl;
    }

    if (!SAFARI_THEME_WEBM_PATTERN.test(src)) {
        return null;
    }

    return src.replace(/\.webm(?=([?#]|$))/i, '.mov');
};

const themeImageMedia = (path) => {
    const url = /^https?:\/\//i.test(path)
        ? path
        : getWheelThemeAssetUrl(path);

    return {
        name: url,
        type: 'image/external',
        url,
        kind: 'image',
    };
};

const themeExternalImageMedia = (url) => ({
    name: url,
    type: 'image/external',
    url,
    kind: 'image',
});

const standardArtworkAppearance = {
    text: {
        strokeWidth: 0.15,
    },
    center: {
        size: 16,
    },
    pointer: {
        source: 'none',
    },
};

const classicArtworkAppearance = {
    ...standardArtworkAppearance,
    overlay: {
        source: 'custom',
    },
    media: {
        overlay: themeImageMedia('/images/wheel-theme-1.svg'),
    },
    text: {
        ...standardArtworkAppearance.text,
        radius: 0.61,
        innerRadius: 0.17,
    },
};

const christmasArtworkAppearance = {
    ...standardArtworkAppearance,
    overlay: {
        source: 'custom',
    },
    media: {
        overlay: themeImageMedia('/images/wheel-theme-2.svg'),
    },
    text: {
        ...standardArtworkAppearance.text,
        radius: 0.63,
        innerRadius: 0.19,
    },
};

const minimalisticArtworkAppearance = {
    ...standardArtworkAppearance,
    overlay: {
        source: 'custom',
    },
    media: {
        overlay: themeImageMedia('/images/wheel-theme-3.svg'),
    },
    text: {
        ...standardArtworkAppearance.text,
        radius: 0.66,
        innerRadius: 0.17,
    },
};

const wheelThemes = {
    classic: {
        name: 'Classic',
        radius: 0.99,
        overlayImage: getWheelThemeAssetUrl('/images/wheel-theme-1.svg'),
        theme,
        appearance: classicArtworkAppearance,
    },
    christmas: {
        name: 'Christmas',
        radius: 0.9,
        overlayImage: getWheelThemeAssetUrl('/images/wheel-theme-2.svg'),
        theme,
        appearance: christmasArtworkAppearance,
    },
    minimalistic: {
        name: 'Minimalistic',
        radius: 0.99,
        overlayImage: getWheelThemeAssetUrl('/images/wheel-theme-3.svg'),
        theme,
        appearance: minimalisticArtworkAppearance,
    },
    neon: {
        name: 'Neon',
        radius: 0.94,
        theme,
        appearance: {
            version: 9,
            wheel: {
                background: '#070b18',
                radius: null,
                debug: false,
            },
            sectors: {
                colorMode: 'palette',
                palette: ['#312e81', '#5b21b6', '#0e7490', '#1d4ed8', '#701a75', '#155e75'],
            },
            text: {
                color: '#ffffff',
                fontFamily: 'Roboto Mono',
                fontSize: 3,
                fontWeight: 800,
                radius: 0.65,
                innerRadius: 0.22,
                offsetX: 0,
                offsetY: 0,
                orientation: 'radial',
                align: 'middle',
                overflow: 'shrink-wrap',
                minFontSize: 1.9,
                maxLines: 2,
                strokeColor: '#000000',
                strokeWidth: 0,
                shadow: {
                    color: '#000000',
                    blur: 1.2,
                    offsetX: 0.5,
                    offsetY: 0.5,
                },
            },
            border: {
                color: '#a5f3fc',
                width: 0.18,
                shadow: {
                    color: 'transparent',
                    blur: 0,
                    offsetX: 0,
                    offsetY: 0,
                },
            },
            dividers: {
                color: '#c4b5fd',
                width: 0.16,
                shadow: {
                    color: 'transparent',
                    blur: 0,
                    offsetX: 0,
                    offsetY: 0,
                },
            },
            overlay: {
                source: 'custom',
                opacity: 1,
                scale: 1.19,
            },
            center: {
                source: 'custom',
                showEmote: true,
                size: 21,
                opacity: 1,
                scale: 1,
                borderRadius: 50,
                background: 'transparent',
                borderColor: 'transparent',
                borderWidth: 0,
            },
            pointer: {
                source: 'custom',
                position: 'right',
                size: 10,
                offset: -1,
                opacity: 1,
                color: '#f8fafc',
                scale: 1,
            },
            idleAnimation: {
                enabled: true,
                duration: 10000,
                rotation: 5,
                scale: 1,
            },
            sounds: {
                tick: {
                    source: 'preset',
                    presetId: 'tick',
                    isBackground: false,
                },
            },
            sectorOverrides: {},
            media: {
                overlay: {
                    ...themeVideoMedia('/auction/themes/neon/neon-frame.webm'),
                    safariUrl: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/neon/neon-frame.mov`,
                },
                center: {
                    ...themeVideoMedia('/auction/themes/neon/neon-center.webm'),
                    safariUrl: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/neon/neon-center.mov`,
                },
                pointer: themeExternalImageMedia(WHEEL_THEME_POINTER_URLS.neon),
                sectors: {},
                sounds: {
                    start: null,
                    tick: null,
                    win: null,
                },
            },
        },
    },
    realisticSpace: {
        name: 'Realistic Space',
        radius: 0.92,
        theme,
        appearance: {
            version: 9,
            wheel: {
                background: '#070b12',
                radius: null,
                debug: false,
            },
            sectors: {
                colorMode: 'palette',
                palette: ['#0f7385', '#6432ba', '#a91e55', '#c84c13', '#a8750d', '#0b7b45'],
            },
            text: {
                color: '#ffffff',
                fontFamily: 'Roboto Mono',
                fontSize: 3,
                fontWeight: 800,
                radius: 0.64,
                innerRadius: 0.23,
                offsetX: 0,
                offsetY: 0,
                orientation: 'radial',
                align: 'middle',
                overflow: 'shrink-wrap',
                minFontSize: 1.9,
                maxLines: 2,
                strokeColor: '#02060a',
                strokeWidth: 0,
                shadow: {
                    color: 'rgba(0, 0, 0, 0.7)',
                    blur: 0.8,
                    offsetX: 0.5,
                    offsetY: 0.5,
                },
            },
            border: {
                color: '#000000',
                width: 1.15,
                shadow: {
                    color: 'rgba(0, 0, 0, 0.8)',
                    blur: 2.7,
                    offsetX: 0,
                    offsetY: 0,
                },
            },
            dividers: {
                color: '#020509',
                width: 0.48,
                shadow: {
                    color: 'rgba(0, 0, 0, 0.75)',
                    blur: 2,
                    offsetX: 0,
                    offsetY: 0,
                },
            },
            overlay: {
                source: 'custom',
                opacity: 1,
                scale: 1.19,
            },
            center: {
                source: 'custom',
                showEmote: true,
                size: 18,
                opacity: 1,
                scale: 1,
                borderRadius: 50,
                background: 'transparent',
                borderColor: 'transparent',
                borderWidth: 0,
            },
            pointer: {
                source: 'custom',
                position: 'top',
                size: 14,
                offset: -4,
                opacity: 1,
                color: '#d7dce0',
                scale: 1,
            },
            idleAnimation: {
                enabled: true,
                duration: 12000,
                rotation: 1.4,
                scale: 1,
            },
            sounds: {
                tick: {
                    source: 'preset',
                    presetId: 'beep',
                    isBackground: false,
                },
            },
            sectorOverrides: {},
            media: {
                overlay: {
                    ...themeVideoMedia('/auction/themes/space/realistic-space-frame.webm'),
                    safariUrl: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/space/realistic-space-frame.mov`,
                },
                center: {
                    ...themeVideoMedia('/auction/themes/space/realistic-space-center.webm'),
                    safariUrl: `${process.env.REACT_APP_CDN_BASE_URL}/auction/themes/space/realistic-space-center.mov`,
                },
                pointer: themeExternalImageMedia(WHEEL_THEME_POINTER_URLS.realisticSpace),
                sectors: {},
                sounds: {
                    start: null,
                    tick: null,
                    win: null,
                },
            },
        },
    },
}

export const BUILT_IN_WHEEL_THEME_IDS = [
    'neon',
    'realisticSpace',
    'classic',
    'christmas',
    'minimalistic',
];

export const DEFAULT_WHEEL_THEME_ID = 'neon';

export const getWheelTheme = (themeId) => (
    wheelThemes[themeId] || wheelThemes[DEFAULT_WHEEL_THEME_ID]
);

// User-created themes must not keep a reference to a mutable built-in object.
// A snapshot keeps their frame and base renderer configuration independent.
export const createWheelThemeSnapshot = (wheelTheme) => {
    const source = wheelTheme || wheelThemes[DEFAULT_WHEEL_THEME_ID];

    return {
        radius: source.radius,
        overlayImage: source.overlayImage,
        theme: {
            ...source.theme,
            text: { ...source.theme?.text },
            border: { ...source.theme?.border },
            dividers: { ...source.theme?.dividers },
        },
    };
};

export default wheelThemes;
